


import React, { useEffect, useState } from 'react';
import callsStyles from '../../pages/calls/calls.module.css';
import { useTwilioCall } from '../../hooks/useTwilioCall';
import Table from '../ui/table';
import TableSkeleton from '../ui/table-skeleton';
import { MyIcons } from '../icons/my-icons';
import { useModal } from '../../contexts/ModalContext';
import { formatDateToSpanish } from '../../utils/dateUtils';
import AddContactSelectorModal from '../modals/add-contact-selector';
import { API_BASE_URL } from '../../config';
import { showErrorToast } from '../../utils/notifications';

// 🔧 Función para formatear números de teléfono
const formatPhoneNumber = (phoneNumber, contactName) => {
  console.log('🔍 formatPhoneNumber recibió:', phoneNumber, 'contactName:', contactName);
  
  if (!phoneNumber) return 'N/A';
  
  // Si tenemos el nombre del contacto, mostrarlo
  if (contactName) {
    return `${contactName} (${phoneNumber})`;
  }
  
  // Si contiene "client:", extraer el nombre del cliente
  if (phoneNumber.includes('client:')) {
    const clientName = phoneNumber.replace('client:', '');
    console.log('👤 Cliente encontrado:', clientName);
    return `👤 ${clientName}`;
  }
  
  // Si es un número de teléfono, formatearlo
  if (phoneNumber.startsWith('+')) {
    console.log('📞 Número de teléfono válido:', phoneNumber);
    return phoneNumber;
  }
  
  // 🔍 Debug: Cualquier otro caso
  console.log('❓ Formato desconocido:', phoneNumber);
  return phoneNumber;
};

// 🔧 Función para determinar el tipo de llamada
const getCallType = (call) => {
  // Usar el campo callType del backend si está disponible
  if (call.callType) {
    switch (call.callType) {
      case 'inbound': return '📞 Entrante';
      case 'outbound': return '📞 Saliente';
      case 'transfer': return '🔄 Transferencia';
      case 'external': return '🔗 Externa';
      case 'unknown': return '❓ Desconocida';
      default: return '❓ Desconocida';
    }
  }
  
  // Fallback al método anterior
  const { from, to } = call;
  if (from && from.includes('client:')) {
    return '📞 Saliente';
  } else if (to && to.includes('client:')) {
    return '📞 Entrante';
  } else {
    return '❓ Desconocida';
  }
};

// 🔧 Función para traducir estados
const translateStatus = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'Completada';
    case 'busy': return 'Ocupado';
    case 'no-answer': return 'Sin respuesta';
    case 'failed': return 'Falló';
    case 'canceled': return 'Cancelada';
    case 'ringing': return 'Sonando';
    case 'in-progress': return 'En curso';
    case 'queued': return 'En cola';
    default: return status || 'Desconocido';
  }
};

// 🔧 Función para obtener color del estado
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return '#28a745';
    case 'busy': return '#ffc107';
    case 'failed': return '#dc3545';
    case 'no-answer': return '#6c757d';
    case 'ringing': return '#007bff';
    case 'in-progress': return '#28a745';
    case 'queued': return '#ffc107';
    default: return '#007bff';
  }
};

export default function TwilioCallLogs() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redialing, setRedialing] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const { call, isDeviceReady } = useTwilioCall();
  const { openModal } = useModal();

  // Redial function: llama realmente usando Twilio
  const handleRedial = async (number) => {
    if (!isDeviceReady) {
      showErrorToast('El dispositivo Twilio no está listo');
      return;
    }
    setRedialing(true);
    try {
      await call(number);
    } catch (err) {
      showErrorToast('Error al llamar: ' + err.message);
    } finally {
      setRedialing(false);
    }
  };

  // Función para manejar el botón "Añadir a contactos"
  const handleAddContact = (phoneNumber) => {
    setSelectedPhoneNumber(phoneNumber);
    setShowAddContactModal(true);
  };

  // Función para manejar la selección del tipo de contacto
  const handleSelectContactType = (type) => {
    // TODO: Aquí se abrirá el modal correspondiente con el número prellenado
    console.log(`Crear contacto tipo: ${type} con número: ${selectedPhoneNumber}`);
    
    // Abrir el modal correspondiente según el tipo
    switch (type) {
      case 'independiente':
        openModal('ContactsModal', { phoneNumber: selectedPhoneNumber });
        break;
      case 'compania':
        openModal('CompanyModal', { phoneNumber: selectedPhoneNumber });
        break;
      case 'operario':
        openModal('OperatorsModal', { phoneNumber: selectedPhoneNumber });
        break;
      case 'comercial':
        openModal('CommercialsModal', { phoneNumber: selectedPhoneNumber });
        break;
      default:
        console.error('Tipo de contacto no reconocido:', type);
    }
    
    setSelectedPhoneNumber(null);
  };

  useEffect(() => {
    async function fetchCalls() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/api/twilio/voice/calls?page=${currentPage}&size=10`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!res.ok) throw new Error('Error al obtener llamadas');
        const data = await res.json();
        console.log('📞 Datos de llamadas recibidos:', data);
        setCalls(data.calls || []);
        setTotalPages(data.totalPages || 0);
        setTotalItems(data.totalItems || 0);
      } catch (err) {
        console.error('Error al obtener llamadas:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchCalls();
  }, [currentPage]);

  // Definir columnas para la tabla Twilio
  const headers = [
    { label: 'Tipo', key: 'callType' },
    { label: 'Número', key: 'phoneNumber' },
    { label: 'Estado', key: 'statusFormatted' },
    { label: 'Fecha/Hora Inicio', key: 'startTime' },
    { label: 'Fin', key: 'endTime' },
    { label: 'Duración', key: 'duration' }
  ];

  // Adaptar datos para la tabla
  const tableData = calls.map(call => {
    const callType = getCallType(call);
    
    let phoneNumber;
    
    if (callType === '🔄 Transferencia') {
      phoneNumber = 'Transferencia entre agentes';
    } else if (call.realPhoneNumber) {
      // ✨ NUEVO: Usar el nombre del contacto si está disponible
      phoneNumber = formatPhoneNumber(call.realPhoneNumber, call.contactName);
    } else {
      // Lógica mejorada según el tipo de llamada
      switch (call.callType) {
        case 'outbound':
          // Para salientes, mostrar el destino (realPhoneNumber contiene el destino)
          phoneNumber = call.realPhoneNumber ? 
            formatPhoneNumber(call.realPhoneNumber) :
            (call.to && call.to.startsWith('+') ? 
              formatPhoneNumber(call.to) : 'Número no disponible');
          break;
        case 'inbound':
          // Para entrantes, mostrar el origen (realPhoneNumber contiene el origen)
          phoneNumber = call.realPhoneNumber ? 
            formatPhoneNumber(call.realPhoneNumber) :
            (call.from && call.from.startsWith('+') ? 
              formatPhoneNumber(call.from) : 'Número no disponible');
          break;
        case 'external':
          // Ya no debería existir - ahora se clasifica como outbound o inbound
          phoneNumber = call.from && call.from.startsWith('+') ? 
            formatPhoneNumber(call.from) : 
            'Número no disponible';
          break;
        default:
          // Fallback general
          phoneNumber = call.from && call.from.startsWith('+') ? 
            formatPhoneNumber(call.from) :
            (call.to && call.to.startsWith('+') ? formatPhoneNumber(call.to) : 
             'Sin número disponible');
      }
    }
    
    return {
      ...call,
      callType,
      phoneNumber,
      from: formatPhoneNumber(call.from),
      to: formatPhoneNumber(call.to),
      startTime: call.startTime ? formatDateToSpanish(call.startTime) + ' ' + new Date(call.startTime).toLocaleTimeString('es-ES') : '-',
      endTime: call.endTime ? formatDateToSpanish(call.endTime) + ' ' + new Date(call.endTime).toLocaleTimeString('es-ES') : '-',
      duration: call.duration ? `${call.duration}s` : '-',
      status: translateStatus(call.status),
      statusFormatted: (
        <span style={{ 
          color: getStatusColor(call.status),
          fontWeight: 'bold' 
        }}>
          {translateStatus(call.status)}
        </span>
      )
    };
  });

  // Renderizar botón Llamar
  const renderActions = (call) => {
    // � Usar realPhoneNumber del backend
    const numberToCall = call.realPhoneNumber;
    const isKnownContact = call.isKnownContact;
    
    // No mostrar botón si no hay número válido para llamar
    if (!numberToCall || !numberToCall.startsWith('+')) {
      return <span style={{ color: '#6c757d', fontSize: '0.8rem' }}>No disponible</span>;
    }
    
    return (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          className={callsStyles.callButton}
          disabled={redialing || !isDeviceReady}
          title={`Llamar a ${numberToCall}`}
          onClick={() => handleRedial(numberToCall)}
        >
          <MyIcons 
            name="phone" 
            width={14} 
            height={14} 
            color={redialing || !isDeviceReady ? "#6c757d" : "#28a745"}
          />
          Llamar
        </button>
        {!isKnownContact && call.contactName === null && (
          <button
            className={callsStyles.callButton}
            title="Añadir a contactos"
            onClick={() => handleAddContact(numberToCall)}
            style={{ backgroundColor: '#007bff' }}
          >
            <MyIcons 
              name="add" 
              width={14} 
              height={14} 
              color="#fff"
            />
          </button>
        )}
      </div>
    );
  };

  return (
    <div>
      {loading && <TableSkeleton rows={10} columns={5} />}
      {error && <p style={{color:'red', fontSize:'1.1rem'}}>Error: {error}</p>}
      {!loading && !error && (
        <>
          <Table
            headers={headers}
            data={tableData}
            renderActions={renderActions}
          />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '20px',
            padding: '10px',
            borderTop: '1px solid #ddd'
          }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0 || loading}
              style={{
                padding: '8px 16px',
                backgroundColor: currentPage === 0 ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPage === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              ← Anterior
            </button>
            <span style={{ color: '#222', fontSize: '0.9rem' }}>
              Página {currentPage + 1} de {totalPages} ({totalItems} registros)
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage >= totalPages - 1 || loading}
              style={{
                padding: '8px 16px',
                backgroundColor: currentPage >= totalPages - 1 ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Siguiente →
            </button>
          </div>
        </>
      )}
      <AddContactSelectorModal
        isOpen={showAddContactModal}
        onClose={() => setShowAddContactModal(false)}
        phoneNumber={selectedPhoneNumber}
        onSelectType={handleSelectContactType}
      />
    </div>
  );
}
